/**
 * Compact horizontal trust-badge row.
 *
 * Site-wide social-proof signal — drops into the footer (every page) and
 * into checkout/pricing surfaces where the user is about to enter
 * payment data. Each badge represents a *verifiable* fact about the
 * platform: no fake ISO/SOC claims, no inflated user counts.
 *
 * Server-component, no client state. Custom inline SVGs to keep the
 * markup independent of any icon library and stable under SSR.
 */
const BADGES = [
  {
    label: 'Hosted in Germany',
    sub: 'AWS Frankfurt · eu-central-1',
    Icon: GermanyIcon,
  },
  {
    label: 'DSGVO-konform',
    sub: 'EU-Datenresidenz · AV-Vertrag',
    Icon: ShieldIcon,
  },
  {
    label: 'SSL / HTTPS only',
    sub: 'TLS 1.3 · HSTS preload',
    Icon: LockIcon,
  },
  {
    label: 'Stripe Payments',
    sub: 'PCI-DSS Level 1 zertifiziert',
    Icon: StripeIcon,
  },
];

export default function TrustBadges() {
  return (
    <ul
      role="list"
      aria-label="Vertrauenssignale"
      className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4"
    >
      {BADGES.map(({ label, sub, Icon }) => (
        <li
          key={label}
          className="flex items-center gap-2.5 rounded-lg border border-gray-200/80 bg-white/60 px-3 py-2.5 backdrop-blur-sm"
        >
          <span
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600"
            aria-hidden="true"
          >
            <Icon />
          </span>
          <span className="min-w-0">
            <span className="block text-xs font-semibold text-gray-900">
              {label}
            </span>
            <span className="block text-[11px] text-gray-500">
              {sub}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function GermanyIcon() {
  // Pin + heart — German hosting
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s-7-7.5-7-13a7 7 0 0 1 14 0c0 5.5-7 13-7 13Z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  );
}

function ShieldIcon() {
  // Shield with checkmark — GDPR compliance
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-0.5 8-4 8-9V6l-8-3Z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function LockIcon() {
  // Closed padlock — TLS/HTTPS
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="5" y="11" width="14" height="10" rx="1.5" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function StripeIcon() {
  // Card with chip — Stripe / card payments
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 11h18" />
      <path d="M7 16h3" />
    </svg>
  );
}
